import React, { useState } from 'react';
import { PlusCircle, Trash2, GripVertical, ChevronDown, ChevronUp } from 'lucide-react';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const emptySlider = () => ({
  key: '',
  label: '',
  options: [
    { label: '', value: '', instruction: '' },
    { label: '', value: '', instruction: '' },
  ],
  default: 0,
});

const emptyInterest = () => ({ label: '', emoji: '' });

// ─── Sub-components ───────────────────────────────────────────────────────────

function SliderCard({ slider, index, onChange, onDelete }) {
  const [expanded, setExpanded] = useState(true);

  const updateField = (field, value) => onChange({ ...slider, [field]: value });

  const updateOption = (i, field, value) => {
    const options = slider.options.map((o, idx) =>
      idx === i ? { ...o, [field]: value } : o
    );
    onChange({ ...slider, options });
  };

  const addOption = () =>
    onChange({ ...slider, options: [...slider.options, { label: '', value: '', instruction: '' }] });

  const removeOption = (i) => {
    const options = slider.options.filter((_, idx) => idx !== i);
    const newDefault = Math.min(slider.default, Math.max(0, options.length - 1));
    onChange({ ...slider, options, default: newDefault });
  };

  return (
    <div className="border border-gray-200 rounded-lg mb-3 bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 cursor-pointer bg-gray-50 rounded-t-lg"
        onClick={() => setExpanded(e => !e)}>
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-gray-400" />
          <span className="font-medium text-gray-700 text-sm">
            {slider.label || `Slider ${index + 1}`}
          </span>
          {slider.key && (
            <span className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded">
              key: {slider.key}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="text-red-400 hover:text-red-600 p-1"
            title="Delete slider"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 py-4 space-y-4">
          {/* Key + Label row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Key <span className="text-gray-400">(sent to AI)</span>
              </label>
              <input
                type="text"
                value={slider.key}
                onChange={e => updateField('key', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                placeholder="e.g. age_group"
                className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Label <span className="text-gray-400">(shown to user)</span>
              </label>
              <input
                type="text"
                value={slider.label}
                onChange={e => updateField('label', e.target.value)}
                placeholder="e.g. Who's exploring?"
                className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>

          {/* Options */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">
              Options
            </label>
            <div className="space-y-3">
              {slider.options.map((opt, i) => (
                <div key={i} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="radio"
                      name={`default_${index}`}
                      checked={slider.default === i}
                      onChange={() => updateField('default', i)}
                      title="Set as default"
                      className="accent-blue-500"
                    />
                    <input
                      type="text"
                      value={opt.label}
                      onChange={e => updateOption(i, 'label', e.target.value)}
                      placeholder="Display label (shown to user)"
                      className="flex-1 border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                    />
                    <input
                      type="text"
                      value={opt.value}
                      onChange={e => updateOption(i, 'value', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                      placeholder="value key"
                      className="w-28 border border-gray-200 rounded-md px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                    />
                    <button
                      onClick={() => removeOption(i)}
                      disabled={slider.options.length <= 2}
                      className="text-red-400 hover:text-red-600 disabled:text-gray-300 p-1"
                      title="Remove option"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <textarea
                    value={opt.instruction || ''}
                    onChange={e => updateOption(i, 'instruction', e.target.value)}
                    placeholder="AI instruction for this option — e.g. 'Speak as a medieval knight. Use thee, thou, hark. Reference honour and chivalry naturally.'"
                    rows={2}
                    className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-xs text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white resize-none"
                  />
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1 ml-1">
              Radio = default &nbsp;·&nbsp; Instruction is sent verbatim to the AI when this option is selected
            </p>
            <button
              onClick={addOption}
              className="mt-2 ml-5 text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1"
            >
              <PlusCircle className="w-3.5 h-3.5" /> Add option
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function InterestChipPreview({ label, emoji }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200 text-sm text-gray-700">
      <span>{emoji}</span>
      <span>{label}</span>
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function StoryAITab({ itinerary, setItinerary }) {
  const sliders = itinerary.story_sliders || [];
  const interests = itinerary.story_interests || [];

  const [newInterest, setNewInterest] = useState(emptyInterest());

  // ── Slider handlers ──────────────────────────────────────────────────────

  const addSlider = () => {
    setItinerary(prev => ({
      ...prev,
      story_sliders: [...(prev.story_sliders || []), emptySlider()],
    }));
  };

  const updateSlider = (i, updated) => {
    const next = sliders.map((s, idx) => (idx === i ? updated : s));
    setItinerary(prev => ({ ...prev, story_sliders: next }));
  };

  const deleteSlider = (i) => {
    const next = sliders.filter((_, idx) => idx !== i);
    setItinerary(prev => ({ ...prev, story_sliders: next }));
  };

  // ── Interest handlers ────────────────────────────────────────────────────

  const addInterest = () => {
    if (!newInterest.label.trim()) return;
    setItinerary(prev => ({
      ...prev,
      story_interests: [...(prev.story_interests || []), {
        label: newInterest.label.trim(),
        emoji: newInterest.emoji.trim() || '•',
      }],
    }));
    setNewInterest(emptyInterest());
  };

  const deleteInterest = (i) => {
    const next = interests.filter((_, idx) => idx !== i);
    setItinerary(prev => ({ ...prev, story_interests: next }));
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="max-w-2xl mx-auto space-y-10 py-2">

      {/* ── Sliders section ── */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-gray-800">Profile Sliders</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              These appear on the "Adjust settings" screen in the app. Each slider value is injected into the AI prompt using its key.
            </p>
          </div>
          <button
            onClick={addSlider}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600"
          >
            <PlusCircle className="w-4 h-4" />
            Add Slider
          </button>
        </div>

        {sliders.length === 0 ? (
          <div className="border-2 border-dashed border-gray-200 rounded-lg py-8 text-center text-gray-400 text-sm">
            No sliders yet. Add one to let visitors personalise their experience.
          </div>
        ) : (
          sliders.map((slider, i) => (
            <SliderCard
              key={i}
              slider={slider}
              index={i}
              onChange={(updated) => updateSlider(i, updated)}
              onDelete={() => deleteSlider(i)}
            />
          ))
        )}
      </section>

      {/* ── Interests section ── */}
      <section>
        <div className="mb-4">
          <h2 className="text-base font-semibold text-gray-800">Interest Chips</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            These appear as selectable tags on the "What are you into?" screen. Selected chips are passed to the AI as interests.
          </p>
        </div>

        {/* Existing chips */}
        {interests.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {interests.map((chip, i) => (
              <div key={i} className="flex items-center gap-1 group">
                <InterestChipPreview label={chip.label} emoji={chip.emoji} />
                <button
                  onClick={() => deleteInterest(i)}
                  className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add new chip */}
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <input
            type="text"
            value={newInterest.emoji}
            onChange={e => setNewInterest(p => ({ ...p, emoji: e.target.value }))}
            placeholder="😀"
            maxLength={4}
            className="w-14 border border-gray-200 rounded-md px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          <input
            type="text"
            value={newInterest.label}
            onChange={e => setNewInterest(p => ({ ...p, label: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && addInterest()}
            placeholder="Label (e.g. The Battle)"
            className="flex-1 border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          <button
            onClick={addInterest}
            disabled={!newInterest.label.trim()}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 disabled:opacity-40"
          >
            <PlusCircle className="w-4 h-4" />
            Add
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1.5">
          Press Enter or click Add · Hover a chip to delete it
        </p>
      </section>

      {/* ── JSON preview ── */}
      {(sliders.length > 0 || interests.length > 0) && (
        <section>
          <h2 className="text-base font-semibold text-gray-800 mb-2">JSON Preview</h2>
          <pre className="bg-gray-900 text-green-400 text-xs rounded-lg p-4 overflow-auto max-h-64">
            {JSON.stringify(
              {
                ...(sliders.length > 0 && { story_sliders: sliders }),
                ...(interests.length > 0 && { story_interests: interests }),
              },
              null,
              2
            )}
          </pre>
        </section>
      )}
    </div>
  );
}
